import { createFileRoute } from '@tanstack/react-router'
import { useEvent, useDashboardStats } from '@/lib/api/queries'
import { Users, ShoppingCart, Package, Coins } from 'lucide-react'
import { formatInEventZone, timeZoneLabel } from '@/lib/eventTime'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { useMemo } from 'react'
import { usePermission } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'

export const Route = createFileRoute('/_authenticated/events/$eventId/dashboard')({
  component: DashboardPage,
})

interface StatCardProps {
  icon: React.ReactNode
  iconBg: string
  label: string
  value: string | number
}

function StatCard({ icon, iconBg, label, value }: StatCardProps) {
  return (
    <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-3 pt-4 md:p-4">
      <div className="flex items-center">
        <div className={`p-2 rounded-md ${iconBg} mr-3`}>{icon}</div>
        <div>
          <p className="text-xs md:text-sm text-neutral-600 dark:text-neutral-400 uppercase">
            {label}
          </p>
          <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
        </div>
      </div>
    </div>
  )
}

function DashboardPage() {
  const { eventId } = Route.useParams()
  const eventIdNum = Number(eventId)
  
  // Permission check - hooks must be called before any early returns
  const canViewDashboard = usePermission('can_view_dashboard')
  const isVendor = usePermission('is_vendor')
  
  // All hooks must be called before conditional returns
  const { data: event, isLoading: eventLoading } = useEvent(eventIdNum)
  const { data: stats, isLoading: statsLoading, error: statsError } = useDashboardStats(eventIdNum)

  // Parse top items - handle both Django ORM field names and simple names
  const topItems = useMemo(() => {
    return (stats?.top_5_item || []).map(item => ({
      name: item.items__vendor_event_item__name || item.name || '-',
      vendor: item.vendor_event__vendor__name || item.vendor_name || '-',
      price: item.items__vendor_event_item__price || item.tokens || 0,
      count: item.count || item.qty || 0,
    }))
  }, [stats?.top_5_item])

  // Parse top locations/vendors - handle both Django ORM field names and simple names
  const topLocations = useMemo(() => {
    return (stats?.top_5_vendor || []).map(vendor => {
      const vendorName = vendor.vendor_event__vendor__name || vendor.name || '-'
      const booth = vendor.vendor_event__booth || vendor.booth || ''
      // Display name: if vendor and booth are different, show both
      const displayName = vendorName === booth || !booth ? vendorName : `${vendorName} - ${booth}`
      return {
        name: displayName,
        tokens: vendor.tokens || vendor.total_tokens || 0,
        revenue: vendor.amount || vendor.revenue || vendor.total_revenue || 0,
      }
    })
  }, [stats?.top_5_vendor])

  // Parse time_data for chart
  const chartData = useMemo(() => {
    if (!stats?.time_data || stats.time_data.length === 0) {
      return []
    }
    return stats.time_data.map(entry => {
      // Parse date format '2024-08-17 20' to 'Aug 17 20:00'
      let formattedDate = entry.date
      try {
        const parts = entry.date.split(' ')
        if (parts.length >= 2) {
          const datePart = parts[0]
          const hourPart = parts[1]
          const date = new Date(`${datePart}T${hourPart}:00:00`)
          formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + 
                         date.toLocaleTimeString('en-US', { hour: 'numeric', hour12: false }) + ':00'
        }
      } catch {
        // Keep original format if parsing fails
      }
      return {
        date: formattedDate,
        amount: entry.amount,
      }
    })
  }, [stats?.time_data])
  
  // Permission guard - now safe to return early since all hooks have been called
  if (!canViewDashboard) {
    return <AccessDenied message="You don't have permission to view the dashboard." />
  }

  const isLoading = eventLoading || statsLoading

  // Parse stats from the API response - handle different response formats
  // customers can be a number or an array [count]
  let customers = 0
  if (stats?.customers) {
    if (Array.isArray(stats.customers) && stats.customers.length > 0) {
      customers = Number(stats.customers[0]) || 0
    } else if (typeof stats.customers === 'number') {
      customers = stats.customers
    }
  }

  const totalOrders = stats?.total_orders || 0
  const totalItems = stats?.items || 0
  
  // Prefer net tokens in circulation (gross issued minus card/credit refunds);
  // fall back to gross tokens only when the admin-only summary isn't present.
  // Must be a presence check (??), NOT truthiness: a fully-refunded event has
  // net 0, which is falsy and would otherwise fall through to the gross total.
  const tokensIssued =
    stats?.token_summary?.net_tokens_in_circulation ??
    stats?.tokens ??
    0
  
  const tokensSpent = (stats?.tokens_spent || 0) - (stats?.token_summary?.order_refunds || 0)

  return (
    <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8">
      <div className="min-h-[calc(100vh-8rem)] flex flex-col py-8">
        {/* Event Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                {event?.name || 'Loading...'}
              </h2>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                {event?.start_date_time && event?.end_date_time && (
                  <>
                    {formatInEventZone(event.start_date_time, event.timezone, 'MMM d, yyyy h:mm a')} -{' '}
                    {formatInEventZone(event.end_date_time, event.timezone, 'MMM d, yyyy h:mm a')}
                    {event.timezone && ` (${timeZoneLabel(event.timezone)})`}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Stat Cards */}
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6 md:mb-8 w-full animate-pulse">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-3 pt-4 md:p-4"
              >
                <div className="flex items-center">
                  <div className="p-2 rounded-md bg-neutral-100 dark:bg-neutral-800 mr-3 w-9 h-9" />
                  <div>
                    <div className="h-3 bg-neutral-200 dark:bg-neutral-800 rounded w-16 mb-2" />
                    <div className="h-5 bg-neutral-200 dark:bg-neutral-800 rounded w-12" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : statsError ? (
          <div className="mb-6 md:mb-8 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-700 dark:text-red-400">Failed to load dashboard stats: {statsError.message}</p>
          </div>
        ) : (
          <div className={`grid grid-cols-2 sm:grid-cols-2 ${isVendor ? 'lg:grid-cols-4' : 'lg:grid-cols-5'} gap-4 mb-6 md:mb-8 w-full`}>
            <StatCard
              icon={<Users className="w-5 h-5 text-blue-500" />}
              iconBg="bg-blue-50 dark:bg-blue-900/20"
              label="CUSTOMERS"
              value={customers}
            />
            <StatCard
              icon={<ShoppingCart className="w-5 h-5 text-red-500" />}
              iconBg="bg-red-50 dark:bg-red-900/20"
              label="ORDERS"
              value={totalOrders}
            />
            <StatCard
              icon={<Package className="w-5 h-5 text-yellow-500" />}
              iconBg="bg-yellow-50 dark:bg-yellow-900/20"
              label="ITEMS"
              value={totalItems}
            />
            {!isVendor && (
              <StatCard
                icon={<Coins className="w-5 h-5 text-purple-500" />}
                iconBg="bg-purple-50 dark:bg-purple-900/20"
                label="TOKENS ISSUED"
                value={tokensIssued}
              />
            )}
            <StatCard
              icon={<Coins className="w-5 h-5 text-green-500" />}
              iconBg="bg-green-50 dark:bg-green-900/20"
              label="TOKENS SPENT"
              value={tokensSpent}
            />
          </div>
        )}

        {/* Revenue Over Time Chart */}
        <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg mb-6">
          <div className="px-6 pt-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Revenue Over Time</h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">Hourly revenue trends in tokens</p>
          </div>
          <div className="p-6">
            {isLoading ? (
              <div className="h-[300px] bg-neutral-50 dark:bg-neutral-900 rounded animate-pulse" />
            ) : chartData.length === 0 ? (
              <div className="h-[300px] bg-neutral-50 dark:bg-neutral-900 rounded flex items-center justify-center">
                <p className="text-neutral-500 dark:text-neutral-400">No revenue data available</p>
              </div>
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-neutral-200 dark:stroke-neutral-800" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 11 }}
                      className="text-neutral-500 dark:text-neutral-400"
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 11 }}
                      className="text-neutral-500 dark:text-neutral-400"
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value: number) => value.toLocaleString()}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--tooltip-bg, white)',
                        border: '1px solid var(--tooltip-border, #e5e5e5)',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                      labelStyle={{ fontWeight: 600 }}
                      formatter={(value) => [(value ?? 0).toLocaleString(), 'Tokens']}
                    />
                    <Line
                      type="monotone"
                      dataKey="amount"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4, fill: '#3b82f6' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Tables Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
          {/* Top Items Table */}
          <div>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Top Items</h3>
            <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
                <thead className="bg-neutral-50 dark:bg-neutral-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Item
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Vendor
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Tokens
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      # Sold
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
                  {isLoading ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-4 text-center text-neutral-500 dark:text-neutral-400">
                        Loading top items...
                      </td>
                    </tr>
                  ) : topItems.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-4 text-center text-neutral-500 dark:text-neutral-400">
                        No items data available
                      </td>
                    </tr>
                  ) : (
                    topItems.map((item, index) => (
                      <tr key={index} className="hover:bg-neutral-50 dark:hover:bg-neutral-900">
                        <td className="px-6 py-2 whitespace-normal text-sm text-neutral-900 dark:text-neutral-100">
                          {item.name}
                        </td>
                        <td className="px-6 py-2 whitespace-normal text-sm text-neutral-500 dark:text-neutral-400">
                          {item.vendor}
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400 text-right">
                          {item.price.toLocaleString()}
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400 text-right">
                          {item.count.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Top Locations Table */}
          <div>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Top Locations</h3>
            <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
                <thead className="bg-neutral-50 dark:bg-neutral-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Tokens
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Revenue ($)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
                  {isLoading ? (
                    <tr>
                      <td colSpan={3} className="px-6 py-4 text-center text-neutral-500 dark:text-neutral-400">
                        Loading top locations...
                      </td>
                    </tr>
                  ) : topLocations.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-6 py-4 text-center text-neutral-500 dark:text-neutral-400">
                        No locations data available
                      </td>
                    </tr>
                  ) : (
                    topLocations.map((location, index) => (
                      <tr key={index} className="hover:bg-neutral-50 dark:hover:bg-neutral-900">
                        <td className="px-6 py-2 whitespace-normal text-sm text-neutral-900 dark:text-neutral-100">
                          {location.name}
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400 text-right">
                          {location.tokens.toLocaleString()}
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400 text-right">
                          ${location.revenue.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
